import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiService from '../../services/apiServices';
import { Card, CardContent, InputAdornment, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography, Stack, Button, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PageHeader from '../common/PageHeader';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { printReport } from '../../utils/pdfPrint';
import SendReportEmailDialog from '../common/SendReportEmailDialog';
import { ExportMenu, ReportPage } from './ReportKit';
import { activeWorkers } from '../../utils/workerStatus';

const EmployeesList = () => {
    const { t } = useTranslation();
    const [loader, setLoader] = useState(true);
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [emailOpen, setEmailOpen] = useState(false);

    const pdfCols = [
      { label: t('report.nr'), key: 'nr', align: 'left', width: '5%' },
      { label: t('report.fullName'), key: 'name', align: 'left', width: '30%' },
      { label: t('report.nid'), key: 'nid', align: 'left', width: '20%' },
      { label: t('report.department'), key: 'dept', align: 'left', width: '25%' },
      { label: t('report.position'), key: 'pos', align: 'left', width: '20%' },
    ];

    const getData = () => {
        setLoader(true);
        apiService().get('workers')
            .then(response => {
                if (response.success) {
                    // Raporti «Lista e punëtorëve» është gjendja e sotme e stafit;
                    // të larguarit e fryjnë numrin dhe dalin te çdo eksport i tij.
                    setItems(activeWorkers(response?.data));
                }
                setLoader(false);
            })
            .catch(() => setLoader(false));
    };

    useEffect(() => {
        getData();
    }, []);

    const fullName = (w) => w?.FullName || `${w?.FirstName || ''} ${w?.LastName || ''}`.trim() || '—';

    const filtered = items.filter((w) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            fullName(w).toLowerCase().includes(q) ||
            String(w?.PersonalNumber || w?.PersonalId || '').toLowerCase().includes(q) ||
            String(w?.DepartmentName || w?.Department || '').toLowerCase().includes(q)
        );
    });

    const pdfRows = filtered.map((w, i) => ({
      nr: i + 1,
      name: fullName(w),
      nid: w.PersonalNumber || w.PersonalId || '—',
      dept: w.DepartmentName || w.Department || '—',
      pos: w.Position || w.JobTitle || w.TypeOfJob || '—',
    }));

    const exportPdf = () => printReport({
      title: t('reports.employeeCard'),
      subtitle: `${t('report.total')}: ${filtered.length} ${t('report.workers')}`,
      orientation: 'portrait',
      columns: pdfCols,
      rows: pdfRows,
    });

    const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <ReportPage>
            <PageHeader
                title={t('reports.employeeCard')}
                actions={<>
                    <ExportMenu onPdf={exportPdf} onEmail={() => setEmailOpen(true)} />
                    <Button component={Link} to="/home/reports" variant="outlined" size="small">{t('report.backLabel')}</Button>
                </>}
            />
            {loader && <LinearProgress sx={{ mb: 2 }} />}
            {!loader && (
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                {filtered.length} {t('report.workers')}
                            </Typography>
                            <TextField
                                size="small"
                                placeholder={t('report.searchByName')}
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ width: 260 }}
                            />
                        </Stack>
                        <TableContainer component={Paper} elevation={0} variant="outlined">
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>{t('report.nr')}</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>{t('report.fullName')}</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>{t('report.nid')}</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>{t('report.department')}</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>{t('report.position')}</TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: 40 }}></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginated.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                <Typography color="text.secondary">{t('report.noData')}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {paginated.map((w, index) => (
                                        <TableRow
                                            key={w.Id || w.id}
                                            hover
                                            sx={{ '&:last-child td': { border: 0 } }}
                                        >
                                            <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                            <TableCell>
                                                <Link
                                                    to={`/home/reports/employee-card/${w.Id || w.id}`}
                                                    style={{ color: 'inherit', textDecoration: 'none', fontWeight: 600 }}
                                                >
                                                    {fullName(w)}
                                                </Link>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={w.PersonalNumber || w.PersonalId || '—'}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>{w.DepartmentName || w.Department || '—'}</TableCell>
                                            <TableCell>{w.Position || w.JobTitle || w.TypeOfJob || '—'}</TableCell>
                                            <TableCell>
                                                <Link to={`/home/reports/employee-card/${w.Id || w.id}`}>
                                                    <ArrowForwardIosIcon fontSize="inherit" sx={{ color: 'text.secondary', fontSize: 14 }} />
                                                </Link>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50]}
                            component="div"
                            count={filtered.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                            labelRowsPerPage={t('report.rowsPerPage')}
                        />
                    </CardContent>
                </Card>
            )}
        <SendReportEmailDialog
          open={emailOpen} onClose={() => setEmailOpen(false)}
          reportTitle={t('reports.employeeCard')}
          subtitle={`${t('report.total')}: ${filtered.length} ${t('report.workers')}`}
          columns={pdfCols} rows={pdfRows}
          workers={items}
          orientation="portrait"
        />
        </ReportPage>
    );
};

export default EmployeesList;
